import React, { useMemo } from 'react';
import { Slate, withReact } from '../core';
import type { Editor, SlateProps } from '../core';
import type { EnhanceProvider } from '../types';
import { usePlugins } from '../hooks';
import compose from 'ramda/es/compose';
import { withOpsLog } from '@sironamedical/slate-ops-log';
import { IdentityFn } from 'config/constants';
import { env } from 'config/env';
import { logger } from 'modules/logger';

const isTestEnv = env.NODE_ENV === 'test' || env.STORYBOOK_STORYSHOTS === 'true';

const enhanceComponent = (
  base: React.ComponentType<SlateProps>,
  enhancers: ReadonlyArray<EnhanceProvider>
): React.ComponentType<SlateProps> => {
  if (enhancers.length === 0) return base;
  return enhancers.reduceRight<React.ComponentType<SlateProps>>((enhanced, enhancer) => {
    const enhancedComponent = enhancer(enhanced);
    return (props: SlateProps) => enhancedComponent(props);
  }, base);
};

export const EnhancedSlate = ({
  editor,
  onSelectionChange,
  onValueChange,
  initialValue,
  children,
}: SlateProps): React.ReactElement => {
  const pluginsBag = usePlugins();
  const { activatedPlugins, getProviderEnhancers, getEditorStateEnhancers } = pluginsBag;

  const editorStateEnhancers = useMemo(() => {
    logger.debug(
      '[EnhancedSlate] getEditorStateEnhancers called with activatedPlugins:',
      activatedPlugins
    );
    return getEditorStateEnhancers();
  }, [getEditorStateEnhancers, activatedPlugins]);
  const providerEnhancers = useMemo(() => {
    logger.debug(
      '[EnhancedSlate] getProviderEnhancers called with activatedPlugins:',
      activatedPlugins
    );
    return getProviderEnhancers();
  }, [getProviderEnhancers, activatedPlugins]);

  const enhancedEditor = useMemo(() => {
    const composeStateEnhancers = compose as (
      ...fns: Array<(e: Editor) => Editor>
    ) => (e: Editor) => Editor;

    return composeStateEnhancers(
      !isTestEnv ? withOpsLog : IdentityFn,
      withReact,
      ...editorStateEnhancers
    )(editor);
  }, [editorStateEnhancers, editor]);

  const EnhancedSlateComponent = useMemo(() => {
    if (editor == null) return Slate;
    return enhanceComponent(Slate, providerEnhancers);
  }, [providerEnhancers, editor]);

  return (
    <EnhancedSlateComponent
      editor={enhancedEditor}
      onSelectionChange={onSelectionChange}
      onValueChange={onValueChange}
      initialValue={initialValue}
    >
      {children}
    </EnhancedSlateComponent>
  );
};
